import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StoriesGrid from "@/components/dashboard/StoriesGrid";
import StoryFilters from "@/components/dashboard/StoryFilters";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import { Input } from "@/components/ui/input";
import * as Icons from "lucide-react";
import { Lock } from "lucide-react";
import { Json } from "@/integrations/supabase/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { logger } from "@/lib/logger";

type Story = {
  id: string;
  title: string;
  genre: string[];
  created_at: string;
  finished_at: string | null;
  characters: { name: string; description: string }[];
};

type DatabaseStory = {
  id: string;
  title: string;
  genre: string[];
  created_at: string;
  finished_at: string | null;
  characters: Json;
};

const StoriesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("ongoing");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [filters, setFilters] = useState({
    genre: [],
    environment: [],
    storyLength: [],
    themes: [],
  });

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
      
      if (user?.id) {
        // Get user's subscription tier
        const { data: subscriptionData } = await supabase
          .from('subscriptions')
          .select('tier')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();
          
        setSubscriptionTier(subscriptionData?.tier || 'free');
      }
    };
    getCurrentUser();
  }, []);

  const { data: featureUsage, isLoading: isLoadingUsage } = useQuery({
    queryKey: ["featureUsage", "daily_story_limit", currentUserId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("check-feature-usage", {
        body: { 
          feature_key: "daily_story_limit",
          user_id: currentUserId 
        }
      });

      if (error) {
        logger.error("Error checking feature usage:", error);
        throw error;
      }

      return data;
    },
    enabled: !!currentUserId,
  });

  const { data: stories, isLoading, refetch } = useQuery({
    queryKey: ["stories", activeTab, filters, searchQuery, currentUserId, sortBy],
    queryFn: async () => {
      let query = supabase
        .from("stories")
        .select("*, profiles(display_name)");

      if (activeTab === "ongoing") {
        query = query.is("finished_at", null).eq("user_id", currentUserId);
      } else if (activeTab === "completed") {
        query = query.not("finished_at", "is", null).eq("user_id", currentUserId);
      } else if (activeTab === "public") {
        query = query.eq("is_public", true);
      }

      if (sortBy === "rating" && (activeTab === "public" || activeTab === "completed")) {
        query = query.order("average_rating", { ascending: false });
      } else if (sortBy === "title") {
        query = query.order("title");
      } else {
        query = query.order("created_at", { ascending: false });
      }

      if (filters.genre.length > 0) {
        query = query.contains("genre", filters.genre);
      }
      if (filters.environment.length > 0) {
        query = query.contains("environment", filters.environment);
      }
      if (filters.storyLength.length > 0) {
        query = query.in("story_length", filters.storyLength);
      }
      if (filters.themes.length > 0) {
        query = query.contains("themes", filters.themes);
      }

      if (searchQuery) {
        // Simple text-based search in both title and characters fields
        query = query.or(`title.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data as (DatabaseStory & { profiles: { display_name: string | null } })[]).map(story => ({
        ...story,
        author: story.profiles?.display_name || 'Anonymous',
        characters: (story.characters as any[] || []).map(char => ({
          name: char.name || '',
          description: char.description || ''
        }))
      }));
    },
    enabled: !!currentUserId,
  });

  const handleNewStory = async () => {
    if (!featureUsage?.can_use) {
      setIsDialogOpen(true);
      return;
    }
    try {
      if (!featureUsage?.can_use) {
        toast({
          title: t('errors.limitReached'),
          description: t('errors.upgradeToCreateMore'),
          variant: "destructive",
        });
        return;
      }

      navigate("/story/new");
    } catch (error) {
      logger.error("Error handling new story:", error);
      toast({
        title: t('errors.error'),
        description: t('errors.tryAgain'),
        variant: "destructive",
      });
    }
  };

  const handleStoryClick = (storyId: string, isCompleted: boolean) => {
    navigate(isCompleted ? `/read/${storyId}` : `/editor/${storyId}`);
  };

  const getUsageColor = () => {
    if (!featureUsage) return "bg-gray-200";
    const percentage = (featureUsage.current_usage / featureUsage.limit) * 100;
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <TooltipProvider>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col w-full md:w-auto gap-2">
                  <Button
                    type="button"
                    onClick={handleNewStory}
                    className={` ${
                      !featureUsage?.can_use ? 'bg-white/10 hover:text-white hover:bg-white/20 w-full md:w-auto' : 'bg-[#7E69AB] hover:bg-[#6E59A5] w-full md:w-auto'
                    }`}
                  >
                    <Icons.BookPlus className="mr-2 h-4 w-4" />
                    {t('dashboard.newStory')}
                    {!featureUsage?.can_use && <Lock className="h-4 w-4" />}
                  </Button>
                  {featureUsage && featureUsage.limit !== -1 && (
                    <div className="w-full md:w-48">
                      <Progress 
                        value={(featureUsage.current_usage / featureUsage.limit) * 100} 
                        className={getUsageColor()}
                      />
                      <p className="text-xs text-white/60 mt-1">
                        {featureUsage.limit - featureUsage.current_usage} / {featureUsage.limit} {t('dashboard.storiesRemaining')}
                      </p>
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{featureUsage?.can_use 
                  ? t('dashboard.storiesAvailable') 
                  : t('dashboard.upgradeForMore')}
                </p>
              </TooltipContent>
            </Tooltip>
            {!featureUsage?.can_use && (
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('settings.subscription.upgradeRequired')}</DialogTitle>
                  <DialogDescription>
                    {t('settings.subscription.upgradeForAccess')}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2 text-black">
                    <Lock className="w-5 h-5 text-primary" />
                    <span>
                      {subscriptionTier === 'free' 
                        ? t('navigation.basicSubscriptionRequired')
                        : t('navigation.premiumSubscriptionRequired')}
                    </span>
                  </div>
                </div>
                <DialogFooter className="sm:justify-start">
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => {
                      setIsDialogOpen(false);
                      navigate("/dashboard/more");
                    }}
                    className="w-full sm:w-auto"
                  >
                    {t('settings.subscription.upgradeNow')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            )}
          </Dialog>
        </TooltipProvider>
      </div>

      <Tabs defaultValue="ongoing" className="w-full" onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 bg-white/5">
          <TabsTrigger value="ongoing" className="data-[state=active]:bg-white/10">
            <Icons.BookOpen className="w-4 h-4 mr-2" />
            {isMobile ? t('dashboard.ongoingStories').split(' ')[0] : t('dashboard.ongoingStories')}
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-white/10">
            <Icons.CheckSquare className="w-4 h-4 mr-2" />
            {isMobile ? t('dashboard.completedStories').split(' ')[0] : t('dashboard.completedStories')}
          </TabsTrigger>
          <TabsTrigger value="public" className="data-[state=active]:bg-white/10">
            <Icons.Globe className="w-4 h-4 mr-2" />
            {isMobile ? t('dashboard.publicStories').split(' ')[0] : t('dashboard.publicStories')}
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Input
              placeholder={t('dashboard.search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white/10 text-white placeholder:text-white/60"
              maxLength={2000}
            />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="bg-white/10 text-white">
                <div className="flex items-center gap-2">
                  <Icons.ArrowUpDown className="h-4 w-4" />
                  <span>{t('dashboard.filters.sortBy')}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="title">{t('dashboard.filters.name')}</SelectItem>
                <SelectItem value="created_at">{t('dashboard.filters.dateCreated')}</SelectItem>
                {(activeTab === "public" || activeTab === "completed") && (
                  <SelectItem value="rating">{t('dashboard.filters.rating')}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <StoryFilters
              filters={filters}
              onFilterChange={setFilters}
            />
          </div>
        </div>

        <TabsContent value="ongoing">
          <StoriesGrid
            stories={stories || []}
            isLoading={isLoading}
            onStoryClick={handleStoryClick}
            refetchStories={() => refetch()}
          />
        </TabsContent>

        <TabsContent value="completed">
          <StoriesGrid
            stories={stories || []}
            isLoading={isLoading}
            isCompleted={true}
            onStoryClick={handleStoryClick}
            refetchStories={() => refetch()}
          />
        </TabsContent>

        <TabsContent value="public">
          <StoriesGrid
            stories={stories || []}
            isLoading={isLoading}
            isCompleted={true}
            onStoryClick={handleStoryClick}
            refetchStories={() => refetch()}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StoriesPage;
